import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Code2, Webhook, ShieldCheck, Zap, ExternalLink, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CURL_SNIPPET = `# 1. Key im Konto anlegen (1×)
#    Einstellungen → Entwickler → "Neuer Key"

# 2. Autorisiert Belege, Rechnungen & Bills ziehen
curl -H "Authorization: Bearer chk_live_xxx" \\
     ${BACKEND_URL}/api/public/v1/invoices

# 3. Webhook für invoice.paid abonnieren →
#    Echtzeit-Sync nach CRM / Buchhaltung / Slack.`;

const FEATURES = [
  {
    icon: Zap,
    title: "Echtzeit-Events",
    text: "7 Webhook-Events (receipt/invoice/bill/journal) mit HMAC-SHA256-Signatur, 3× Retry mit exponential backoff.",
  },
  {
    icon: Webhook,
    title: "Self-Service-Keys",
    text: "Kunden legen eigene API-Keys direkt im UI an. Per-Key Permissions (read/write/admin), SHA256-Hash in der DB.",
  },
  {
    icon: ShieldCheck,
    title: "GeBüV-konform",
    text: "Schweizer Buchhaltungsregeln (OR 957a, GeBüV Audit-Trail, MWST-Saldo/Jährlich) bereits im Datenmodell.",
  },
  {
    icon: Code2,
    title: "Postman-Ready",
    text: "OpenAPI 3.1, ReDoc, Swagger UI und Postman-Collection als Ein-Klick-Import. In 30 Sekunden produktiv.",
  },
];

export function Partner() {
  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Kopiert.");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/40" data-testid="partner-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="partner-home-link">
            <div className="h-8 w-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold">B</div>
            <span className="font-semibold text-slate-900">Buchhaltung CH</span>
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/login" className="text-slate-600 hover:text-slate-900" data-testid="partner-login-link">Login</Link>
            <Button size="sm" asChild data-testid="partner-cta-signup">
              <Link to="/register">Kostenfrei testen <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <Badge className="mb-4 bg-indigo-100 text-indigo-800 hover:bg-indigo-100" data-testid="partner-badge">
          Für Treuhänder & ERP-Anbieter
        </Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 max-w-4xl">
          Stecke unsere Schweizer Buchhaltung in{" "}
          <span className="text-indigo-600">deinen Stack</span>.
        </h1>
        <p className="mt-6 text-base text-slate-600 max-w-2xl">
          REST-API v1, signierte Webhooks und OpenAPI-Doku — zertifiziert gegen GeBüV und MWST-Recht.
          Ideal für Treuhänder, die Mandate bündeln wollen, und ERP-Anbieter mit Schweizer Kundschaft.
        </p>

        <div className="mt-8 flex flex-wrap gap-3" data-testid="partner-hero-ctas">
          <Button size="lg" asChild data-testid="partner-cta-register">
            <Link to="/register">Kostenfrei testen <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
          <Button size="lg" variant="outline" asChild data-testid="partner-cta-redoc">
            <a href={`${BACKEND_URL}/api/redoc`} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> API-Doku (ReDoc)
            </a>
          </Button>
          <Button size="lg" variant="ghost" asChild data-testid="partner-cta-status">
            <a href={`${BACKEND_URL}/api/public/v1/status`} target="_blank" rel="noopener noreferrer">
              API-Status
            </a>
          </Button>
        </div>
      </section>

      {/* Code Snippet */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <Card className="p-5 bg-slate-900 border-slate-700 relative" data-testid="partner-code-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-slate-300">
              <Code2 className="h-4 w-4" />
              <span className="text-xs font-mono">Quickstart · Bash</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copy(CURL_SNIPPET)}
              className="text-slate-300 hover:text-white"
              data-testid="partner-copy-curl"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Kopieren
            </Button>
          </div>
          <pre className="text-slate-100 text-xs sm:text-sm overflow-x-auto font-mono leading-relaxed" data-testid="partner-curl-code">
            {CURL_SNIPPET}
          </pre>
        </Card>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="partner-features">
          {FEATURES.map((f, i) => (
            <Card key={i} className="p-5 border-slate-200 bg-white" data-testid={`partner-feature-${i}`}>
              <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center mb-3">
                <f.icon className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">{f.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{f.text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Developer Resources */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Entwickler-Ressourcen</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="partner-resources">
          <ResourceLink
            href={`${BACKEND_URL}/api/docs`}
            title="Swagger UI"
            subtitle="Interaktive API-Exploration"
            testid="resource-swagger"
          />
          <ResourceLink
            href={`${BACKEND_URL}/api/redoc`}
            title="ReDoc"
            subtitle="Schön formatierte Referenz"
            testid="resource-redoc"
          />
          <ResourceLink
            href={`${BACKEND_URL}/api/public/v1/postman-collection.json`}
            title="Postman Collection"
            subtitle="Direkt-Import (v2.1)"
            testid="resource-postman"
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center border-t border-slate-200">
        <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-3" data-testid="partner-final-heading">
          Lass uns deinen Treuhänder-Stack verbinden.
        </h2>
        <p className="text-sm text-slate-600 mb-6 max-w-xl mx-auto">
          14 Tage kostenfrei testen, eigene API-Keys in 30 Sekunden, keine Einrichtungsgebühr.
        </p>
        <Button size="lg" asChild data-testid="partner-final-cta">
          <Link to="/register">
            Jetzt starten <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </section>

      <footer className="border-t border-slate-200 text-center py-6 text-xs text-slate-500">
        © {new Date().getFullYear()} Buchhaltung CH · <Link to="/" className="hover:text-slate-900">Startseite</Link> · <Link to="/impressum" className="hover:text-slate-900">Impressum</Link> · Powered by <a href="https://www.invictaflow.ch" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">InvictaX Group GmbH</a>
      </footer>
    </div>
  );
}

function ResourceLink({ href, title, subtitle, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-4 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 hover:border-indigo-300 transition-colors group"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-700">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-indigo-600" />
      </div>
    </a>
  );
}

export default Partner;
